import React, { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from 'flow/components/ui-components/Icon';
import ContextMenu from 'flow/components/ContextMenu';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch } from 'flow/hooks/useRedux';
import {
    muteAllParticipants,
    turnOffAllParticipantsVideo,
} from 'flow/actions/meeting-actions';

interface Props {
    anyOtherAudioActive: boolean;
    anyOtherVideoActive: boolean;
    everyoneAudioInactive: boolean;
    everyoneVideoInactive: boolean;
}

/**
 * ⋮ menu beside "Add participants" with room-wide host controls (VID-53 AC2):
 * "Mute all" and "Turn off all cameras". Always shown; each item is disabled
 * (greyed out) once no other participant has that track on, rather than
 * hiding the whole button — same convention as the per-participant menu. The
 * server excludes the requester and the active screen-sharer; targets can
 * re-enable afterwards. The "All muted"/"All cameras off" copy is a claim
 * about the whole room though, so it's driven by everyone's state (self
 * included), not just the "other participants" gate the button itself uses.
 */
const MeetingAllParticipantsMenu = ({
    anyOtherAudioActive,
    anyOtherVideoActive,
    everyoneAudioInactive,
    everyoneVideoInactive,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const muteAll = useCallback(
        () => dispatch(muteAllParticipants()),
        [dispatch],
    );
    const turnOffAllCameras = useCallback(
        () => dispatch(turnOffAllParticipantsVideo()),
        [dispatch],
    );

    return (
        <ContextMenu
            triggerLabel={i18next.t('videoconference.all_participants_menu')}
            className="flex size-12 shrink-0 items-center justify-center !rounded-xl !bg-gray-700 !p-0 hover:!bg-gray-600"
            menuClassName="no-dark min-w-[200px] !bg-gray-700 text-white outline-none"
            placement="top-end"
            icon={<Icon iconKey="more_vert" className="text-white" />}
            dataTestId="participant-list-all-menu"
        >
            <MenuItem
                onClick={muteAll}
                disabled={!anyOtherAudioActive}
                closeOnClick={true}
                className={twMerge(
                    '!bg-gray-700',
                    anyOtherAudioActive
                        ? '!text-white hover:!bg-gray-600'
                        : '!text-gray-500',
                )}
                dataTestId="participant-list-mute-all"
            >
                <Icon
                    iconKey={everyoneAudioInactive ? 'mic_off' : 'mic_outlined'}
                    className="me-1.5"
                />
                <div>
                    {i18next.t(
                        everyoneAudioInactive
                            ? 'videoconference.host_controls.all_muted'
                            : 'videoconference.host_controls.mute_all',
                    )}
                </div>
            </MenuItem>
            <MenuItem
                onClick={turnOffAllCameras}
                disabled={!anyOtherVideoActive}
                closeOnClick={true}
                className={twMerge(
                    '!bg-gray-700',
                    anyOtherVideoActive
                        ? '!text-white hover:!bg-gray-600'
                        : '!text-gray-500',
                )}
                dataTestId="participant-list-turn-off-all-cameras"
            >
                <Icon
                    iconKey={everyoneVideoInactive ? 'videocam_off' : 'video'}
                    className="me-1.5"
                />
                <div>
                    {i18next.t(
                        everyoneVideoInactive
                            ? 'videoconference.host_controls.all_cameras_off'
                            : 'videoconference.host_controls.turn_off_all_cameras',
                    )}
                </div>
            </MenuItem>
        </ContextMenu>
    );
};

export default MeetingAllParticipantsMenu;
